import Badge from 'src/common/ui-kit/Badge'
import React, { useEffect, useState } from 'react'
import CustomPercentage from '../../../pages/TradePage/components/LimitOrder/CustomPercentage'
import useBreakpoint from 'src/common/hooks/ui/useBreakpoint'
import { DotcEnums } from 'src/common/contracts/enums/dotc'

interface PriceBadgesProps {
  percentage: number | null
  percentageType: DotcEnums.PercentageType | null
  isDynamic: boolean
  onChange?: (value: number | null) => void
  onChangePercentageType?: (value: DotcEnums.PercentageType | null) => void
  className?: string
}

type PlusOrMinus = '+' | '-'

const plusAndMinusBadges: PlusOrMinus[] = ['+', '-']
const plusAndMinusBadgesMap: Record<PlusOrMinus, DotcEnums.PercentageType> = {
  '+': DotcEnums.PercentageType.Plus,
  '-': DotcEnums.PercentageType.Minus,
}

const marketPercentage: [string, number] = ['Market', 0]

// Define the price percentages as an array of tuples [text, value]
const pricePercentages: [string, number][] = [
  [`1%`, 1],
  [`5%`, 5],
  [`10%`, 10],
  [`20%`, 20],
]

const PriceBadges: React.FC<PriceBadgesProps> = ({
  percentage,
  percentageType,
  isDynamic,
  onChange,
  onChangePercentageType,
  className,
}) => {
  const [customPercentageIsActive, setCustomPercentageIsActive] =
    useState(false)
  const [customPercentage, setCustomPercentage] = useState<number | null>(null)

  const isSm = useBreakpoint('sm')

  useEffect(() => {
    if (percentage === null) return

    const percentageInBadges = [marketPercentage, ...pricePercentages].find(
      ([, value]) => value === percentage
    )

    if (!percentageInBadges) {
      setCustomPercentage(percentage)
      setCustomPercentageIsActive(true)
    }
  }, [percentage])

  const handlePercentageChange = (value: number) => {
    onChange?.(value)
    setCustomPercentage(null)
    setCustomPercentageIsActive(false)
  }

  const handleCustomPercentageChange = (percentage: number | null) => {
    if (percentage === null) return
    onChange?.(percentage)
    setCustomPercentage(percentage)
  }

  const handleCustomPercentageClick = () => {
    setCustomPercentageIsActive(true)
  }

  const handleCustomPercentageBlur = () => {
    if (!customPercentage) {
      setCustomPercentageIsActive(false)
      setCustomPercentage(null)
      onChange?.(0)
    }
  }

  const handlePlusOrMinusClick = (percentageType: DotcEnums.PercentageType) => {
    if (percentage === null) return
    onChangePercentageType?.(percentageType)
    if (percentageType === DotcEnums.PercentageType.Minus && percentage > 100) {
      onChange?.(100)
    }
  }

  const getPercentageBadgeIsActive = (value: number) => {
    return (
      percentage !== null && value === percentage && !customPercentageIsActive
    )
  }

  return (
    <div className={className}>
      <Badge
        key={marketPercentage[1]}
        color='secondary'
        size='sm'
        active={marketPercentage[1] === percentage && !customPercentageIsActive}
        className='text-main-accent'
        onClick={() => handlePercentageChange(marketPercentage[1])}
      >
        {marketPercentage[0]}
      </Badge>
      <div className='flex flex-col gap-0.5'>
        {plusAndMinusBadges.map(value => (
          <Badge
            key={value}
            color='secondary'
            size='sm'
            active={percentageType === plusAndMinusBadgesMap[value]}
            className='text-main-accent rounded-full p-0'
            onClick={() => handlePlusOrMinusClick(plusAndMinusBadgesMap[value])}
            style={{ lineHeight: '10px', width: '12px', height: '12px' }}
          >
            <div style={{ lineHeight: '10px', height: '12px' }}>{value}</div>
          </Badge>
        ))}
      </div>
      {pricePercentages
        .slice(0, isSm ? 3 : pricePercentages.length)
        .map(([text, value]) => (
          <Badge
            key={value}
            color='secondary'
            size='sm'
            active={getPercentageBadgeIsActive(value)}
            className='text-main-accent'
            onClick={() => handlePercentageChange(value)}
          >
            {text}
          </Badge>
        ))}
      <CustomPercentage
        isDynamic={isDynamic}
        value={customPercentage}
        onChange={handleCustomPercentageChange}
        onClick={handleCustomPercentageClick}
        onBlur={handleCustomPercentageBlur}
        active={customPercentageIsActive}
        isNegative={percentageType === DotcEnums.PercentageType.Minus}
      />
    </div>
  )
}

export default PriceBadges
